import { useState, useEffect, useRef } from "react";
import { FlagCheckered, Trophy, CaretDown, CaretUp } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAG_COLORS = {
  "Green": "bg-[#34C759]",
  "Yellow": "bg-[#FFD60A]",
  "Red": "bg-[#FF3B30]",
  "Checkered": "bg-white",
  "Pre-Race": "bg-white/30",
};

const MFR_ABBR = { "Tyt": "Toyota", "Chv": "Chevy", "Frd": "Ford" };

export default function NASCARRacePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDriver, setExpandedDriver] = useState(null);
  const pollRef = useRef(null);

  const fetchData = () => {
    axios.get(`${API}/nascar-live`).then((r) => {
      if (r.data.race) setData(r.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    pollRef.current = setInterval(fetchData, 10000);
    return () => clearInterval(pollRef.current);
  }, []);

  if (loading) return (
    <div className="space-y-3">
      {[...Array(8)].map((_, i) => <div key={i} className="h-14 bg-[#141414] rounded-lg animate-pulse" />)}
    </div>
  );

  if (!data || !data.race) return (
    <div className="text-center py-12 text-white/30 text-sm">No live NASCAR data available</div>
  );

  const { race, drivers } = data;
  const leader = drivers[0];

  return (
    <div className="space-y-4" data-testid="nascar-race-panel">
      {/* Race header */}
      <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FlagCheckered size={20} className="text-white/60" />
            <div>
              <h2 className="font-['Bebas_Neue'] text-xl sm:text-2xl uppercase tracking-wider text-white">{race.name}</h2>
              <div className="flex items-center gap-3 mt-0.5">
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase ${FLAG_COLORS[race.flagState] || "bg-white/20"} ${race.flagState === "Yellow" ? "text-black" : race.flagState === "Checkered" ? "text-black" : "text-white"}`}>
                  {race.flagState}
                </span>
                <span className="text-xs text-white/50 score-font">Lap {race.lap} / {race.totalLaps}</span>
                <span className="text-xs text-white/30">{race.lapsToGo} to go</span>
              </div>
            </div>
          </div>
          {/* Lap progress bar */}
          <div className="hidden sm:block w-32">
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#007AFF] rounded-full transition-all" style={{ width: `${(race.lap / race.totalLaps) * 100}%` }} />
            </div>
            <p className="text-[9px] text-white/30 text-right mt-0.5">{Math.round((race.lap / race.totalLaps) * 100)}% complete</p>
          </div>
        </div>
      </div>

      {/* Leaderboard table */}
      <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden" data-testid="nascar-leaderboard">
        {/* Table header */}
        <div className="grid grid-cols-[40px_1fr_80px_80px_80px_70px_80px_60px_60px] px-3 py-2 border-b border-white/10 text-[9px] sm:text-[10px] tracking-widest uppercase text-white/40 font-bold">
          <span>Pos</span>
          <span></span>
          <span className="text-right">Behind</span>
          <span className="text-right">Last Lap</span>
          <span className="text-right hidden md:block">Avg Pos</span>
          <span className="text-right hidden lg:block">Laps Led</span>
          <span className="text-right">Best Spd</span>
          <span className="text-right hidden sm:block">Pos +/-</span>
          <span className="text-right hidden sm:block">Pit</span>
        </div>

        {/* Driver rows */}
        <div className="divide-y divide-white/5">
          {drivers.map((d, idx) => {
            const isLeader = d.position === 1;
            const isExpanded = expandedDriver === idx;
            const posChange = d.position < (drivers.length - idx) ? 0 : 0; // placeholder

            return (
              <div key={idx}>
                <div
                  className={`grid grid-cols-[40px_1fr_80px_80px_80px_70px_80px_60px_60px] px-3 py-2.5 items-center hover:bg-white/5 transition-colors cursor-pointer ${isLeader ? "bg-white/[0.03]" : ""}`}
                  onClick={() => setExpandedDriver(isExpanded ? null : idx)}
                  data-testid={`driver-row-${d.position}`}
                >
                  {/* Position */}
                  <span className={`score-font text-sm ${isLeader ? "text-[#007AFF] font-bold" : "text-white/50"}`}>
                    {d.position}
                  </span>

                  {/* Driver name + car */}
                  <div className="min-w-0">
                    <p className={`text-xs sm:text-sm font-bold uppercase truncate ${isLeader ? "text-white" : "text-white/80"}`}>
                      {d.name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] text-[#007AFF] font-bold">#{d.carNumber}</span>
                      <span className="text-[10px] text-white/30">{MFR_ABBR[d.manufacturer] || d.manufacturer}</span>
                    </div>
                  </div>

                  {/* Behind */}
                  <span className="text-right score-font text-xs text-white/60">
                    {isLeader ? "LEADER" : `${d.behind > 0 ? "+" : ""}${d.behind.toFixed(3)}`}
                  </span>

                  {/* Last Lap */}
                  <span className="text-right score-font text-xs text-white/50">{d.lastLap > 0 ? d.lastLap.toFixed(3) : "-"}</span>

                  {/* Avg Running Pos */}
                  <span className="text-right score-font text-xs text-white/40 hidden md:block">{d.avgRunningPos > 0 ? d.avgRunningPos.toFixed(1) : "-"}</span>

                  {/* Laps Led */}
                  <span className="text-right score-font text-xs text-white/40 hidden lg:block">{d.lapsLed || "-"}</span>

                  {/* Best Speed */}
                  <span className="text-right score-font text-xs text-white/50">{d.bestSpeed > 0 ? d.bestSpeed.toFixed(1) : "-"}</span>

                  {/* Pos +/- (position vs qualifying) */}
                  <span className="text-right text-xs hidden sm:block">
                    {d.status === 1 ? (
                      <span className="text-white/40">{d.pitStops}</span>
                    ) : (
                      <span className="text-[#FF3B30]">OUT</span>
                    )}
                  </span>

                  {/* Last Pit */}
                  <span className="text-right score-font text-xs text-white/30 hidden sm:block">
                    {d.lastPitLap > 0 ? d.lastPitLap : "-"}
                  </span>
                </div>

                {/* Expanded driver detail */}
                {isExpanded && (
                  <div className="px-4 py-3 bg-white/[0.02] border-t border-white/5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs animate-fade-in">
                    <div>
                      <p className="text-[10px] text-white/30 uppercase tracking-wider">Avg Speed</p>
                      <p className="score-font text-white/70 mt-0.5">{d.avgSpeed > 0 ? `${d.avgSpeed} mph` : "-"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/30 uppercase tracking-wider">Laps Completed</p>
                      <p className="score-font text-white/70 mt-0.5">{d.lapsCompleted}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/30 uppercase tracking-wider">Pit Stops</p>
                      <p className="score-font text-white/70 mt-0.5">{d.pitStops}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/30 uppercase tracking-wider">Last Pit Lap</p>
                      <p className="score-font text-white/70 mt-0.5">{d.lastPitLap > 0 ? `Lap ${d.lastPitLap}` : "-"}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
